import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const HighTechSolutions = () => {
  const phrases = [
    "Business Optimization",
    "Process Outsourcing"
  ];

  const cardData = [
    { type: 'purple-clay', content: '📊' },
    { type: 'smooth-wave', content: '📞' },
    { type: 'abstract-blocks', content: '⚙️' },
    { type: 'pink-sphere', content: '📈' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "📞", title: "Omnichannel Customer Success", desc: "Manage inbound and outbound customer experience workflows across voice, email, chat, and digital channels via smart CRM loops." },
    { icon: "⚙️", title: "Back-Office Process Management", desc: "Streamline high-volume operational items including specialized transactional processing, record audits, and systematic data refinement." },
    { icon: "📊", title: "Finance & Accounting Operations", desc: "Delegate end-to-end accounts payable, structured client billing trackers, general ledger balance sheets, and tax compliance sheets safely." },
    { icon: "🧠", title: "Knowledge Process Outsourcing", desc: "Access high-tier research frameworks, financial market tracking metrics, intellectual property checks, and advanced data analytics blocks." },
    { icon: "👔", title: "HR Operations & Talent Management", desc: "Outsource corporate payroll processing, initial talent screening paths, employee record lifecycles, and benefits setup protocols." },
    { icon: "🔄", title: "Workflow Automation & RPA", desc: "Integrate Robotic Process Automation routines to offload mechanical, repetitive manual tasks into high-speed digital worker cycles." },
    { icon: "🛡️", title: "Risk Mitigation & Data Compliance", desc: "Enforce strict global quality assurance paradigms, transaction reviews, and secure access parameters to safeguard processing chains." }
  ];

  const advancedServices = [
    { icon: "📈", title: "Predictive Analytics & Reports", desc: "Transform routine business data points into actionable executive trends using advanced operational metrics and AI tools." },
    { icon: "👥", title: "Dedicated Pod Scaling Models", desc: "Deploy variable specialist teams trained specifically in your software stacks and unique internal corporate logic." },
    { icon: "🌍", title: "Multilingual Operational Bridges", desc: "Deliver cross-border support solutions featuring fluent customer interactions in primary target languages across time zones." },
    { icon: "🗂️", title: "Document Digitize Systems", desc: "Convert legacy physical or unorganized data frameworks into searchable, structured cloud file indices securely." },
    { icon: "🎯", title: "Targeted Customer Acquisition", desc: "Accelerate outbound communication pipelines utilizing automated dialers, lead validation steps, and verified scheduling hooks." },
    { icon: "🔧", title: "Tech-Enabled Service Hubs", desc: "Combine direct staff execution layers with modern ticketing desks to cut ticket resolution latency in half." },
    { icon: "⚖️", title: "SLA Metric Tracking Shield", desc: "Enforce complete transparency across core operational deliverables with automated dashboard monitoring panels." },
    { icon: "🛣️", title: "Supply Chain Coordination Support", desc: "Coordinate third-party vendor schedules, track dynamic transit bills, and monitor real-time shipping milestones." },
    { icon: "🧪", title: "Continuous Process Auditing", desc: "Conduct regular efficiency stress tests to remove procedural waste and maximize daily transactional throughput." }
  ];

  const technologies = {
    "CRM & Communication": ["Salesforce / Zendesk", "HubSpot Systems", "Talkdesk / Genesys Cloud", "Intercom Engagement"],
    "Automation & RPA": ["UiPath Platforms", "Automation Anywhere", "Microsoft Power Automate", "Zapier Enterprise Loops"],
    "Analytics & Records": ["Microsoft PowerBI", "Tableau Analytics", "Excel Engine Frameworks", "Looker Studio Blocks"],
    "HRM & Financial Tools": ["Workday Enterprise", "QuickBooks / Xero Stack", "NetSuite Financials", "ADP Payroll Portal"],
    "Collaboration Ecosystem": ["Slack / MS Teams Suite", "Jira Project Control", "Asana Workflow Boards", "Secure SharePoint Links"]
  };

  const processSteps = [
    { num: "01", title: "Workflow Discovery", desc: "Mapping current manual processes, pinpointing functional inefficiencies, and defining baseline volume metrics." },
    { num: "02", title: "SLA Frame Definition", desc: "Establishing core key performance indicators, accuracy thresholds, turnaround limits, and boundary guidelines." },
    { num: "03", title: "System Transition Plan", desc: "Setting up secure workspace pathways, software environment privileges, and data transfer protection protocols." },
    { num: "04", title: "Specialist Onboarding", desc: "Training dedicated service personnel on unique business rule definitions, tone protocols, and tool sets." },
    { num: "05", title: "Pilot Execution Run", desc: "Launching low-volume operational cycles under close observation to eliminate procedural issues early." },
    { num: "06", title: "Full Scale Deployment", desc: "Scaling daily transactional processing capacity up to corporate expectations with complete structural stability." },
    { num: "07", title: "Continuous Improvement", desc: "Reviewing delivery quality metrics weekly, identifying processing bottlenecks, and deploying automation optimizations." }
  ];

  const industries = [
    "Banking & Credit Networks", "Global Insurance Carriers", "Healthcare & Payer Portals", "E-Commerce Logistics Hubs",
    "Telecommunications Groups", "Real Estate & Asset Holdings", "Legal & Compliance Practices", "Retail & Consumer Networks",
    "Logistics & Shipping Systems", "High-Tech Software Firms", "Travel & Hospitality Chains", "Energy & Utility Suppliers"
  ];

  const designBenefits = [
    "Significant Cost Optimization", "Elastic Resource Scalability", "Strict Contractual Accuracy",
    "24/7 Execution Continuance", "Sharpened Core Strategic Focus", "Automated Task Offloading",
    "Enhanced Consumer Experience", "Standardized Operational Auditing"
  ];

  const whyChooseUs = [
    "Certified Operational Directors", "State-of-the-Art RPA Frameworks", "Uncompromising Data Privacy Mandates",
    "Flawless Complex Service Transfers", "Deep Cross-Domain Processing Prowess", "Flexible Resource Capacity Scaling",
    "Data-Driven Workflow Optimizations", "Completely Transparent Metric Reports", "Proven Enterprise SLA Maintenance",
    "Dedicated Quality Evaluation Pods", "Modern Omnichannel Digital Suites", "100% Operational Compliance Guarantee"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          High Tech Solutions | Technology Consulting & Digital Transformation | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="High Tech Solutions | Technology Consulting & Digital Transformation | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. delivers High Tech Solutions, including technology consulting, enterprise software development, cloud solutions, AI & machine learning, IoT, ERP implementation, automation, data analytics, and digital transformation services for high-tech businesses."
        />

        <meta
          name="keywords"
          content="High Tech Solutions, Technology Consulting, Enterprise Software Development, Digital Transformation, Cloud Solutions, Artificial Intelligence, AI Solutions, Machine Learning, IoT Solutions, ERP Solutions, Business Automation, Data Analytics, Smart Technology Solutions, Software Development Services, High Tech Industry Solutions, Enterprise Technology Services, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/high-tech-solutions"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="High Tech Solutions | Technology Consulting & Enterprise Software | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Accelerate innovation with Affsoft High Tech Solutions. We provide enterprise software development, cloud solutions, AI, machine learning, IoT, ERP implementation, automation, and digital transformation services."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/high-tech-solutions"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="High Tech Solutions | Technology Consulting & Digital Transformation | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Empower your business with Affsoft High Tech Solutions. Our expertise includes enterprise software, cloud computing, AI, IoT, ERP, automation, data analytics, and digital transformation."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="process-outsourcing-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0a1916' // dark operational environment fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.15 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>

          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Business Optimization &nbsp; and &nbsp;</span>
              <span>Process Outsourcing </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-success fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">High Tech Solutions </h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At High Tech Solutions, we transform intricate transactional workflows into high-efficiency operational assets. Our advanced outsourcing frameworks blend dedicated domain specialists, modern software suites, and strict SLA controls to reduce execution costs and optimize enterprise throughput.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>About High Tech Solutions</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-success fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-success px-3 py-2 rounded-pill mb-2 text-uppercase">Operational Excellence</span>
              <h2 className="fw-bold text-dark display-6">Streamlined Operations Tailored for Measurable Efficiency</h2>
              <p className="text-muted mt-3">
                Managing non-core processing workflows internally drains critical strategic resources and raises overhead. At High Tech Solutions, we apply proven execution paradigms to secure data flows and accelerate daily business processes.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-success">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Operational Benefits Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Transition to Managed Operations Models?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED BUSINESS SYSTEMS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">Intelligent Execution</span>
              <h2 className="fw-bold text-dark display-6">Performance Standards Backed by Rigorous Analytics</h2>
              <p className="text-muted mt-3">
                We leverage modern digital tools to add analytical precision to every outsourced task. From multilingual support desks to automated balance matching, our services deliver stable corporate infrastructure built to protect your reputation.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-success text-white px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Enterprise Tech Stack</span>
              <h2 className="fw-bold display-6">Software Infrastructure & Platforms We Deploy</h2>
              <p className="text-light-50">We run complex processing tasks using industry-standard secure operational suites.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-success mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-success opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Methodical Transition</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Outsourcing Lifecycle</h2>
              <p className="text-muted">A transparent operational roadmap that securely moves processes from initial scope to stabilized metrics.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-success border-4 h-100 shadow-sm">
                    <div className="fw-bold text-success display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE HIGH TECH SOLUTIONS ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with High Tech Solutions?</h2>
              <p className="text-muted">We combine strict data governance metrics with elite domain expertise to support your operations.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-success fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default HighTechSolutions;